import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'

import { TreeNode } from 'primeng/api'
import { TooltipModule } from 'primeng/tooltip'
import { TreeModule } from 'primeng/tree'
import { TreeSelectModule } from 'primeng/treeselect'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  WorkspaceAction,
  WorkspaceObject,
} from '@flowx/designer/modules/analytics/analytics-workspace.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import { WorkspaceDefinition } from '@flowx/designer/modules/workspaces/shared/commons.types'
import { WorkspaceEnvTypeTagComponent } from '@flowx/designer/modules/workspaces/shared/workspace-env-type-tag/workspace-env-type-tag.component'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'

import { LastUsedWorkspaceService } from '../../services/last-used-workspace.service'
import { WorkspacesAndPermissionsStore } from '../../workspaces-and-permissions.store'

@Component({
  selector: 'flx-workspaces-switch',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlxIconModule,
    TreeModule,
    TreeSelectModule,
    WorkspaceEnvTypeTagComponent,
    FormsModule,
    TooltipModule,
  ],
  templateUrl: './workspaces-switch.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./workspaces-switch.component.scss'],
})
export class WorkspacesSwitchComponent {
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #navigationService = inject(FlxNavigationService)
  #analyticsService = inject(AnalyticsService)
  #lastUsedWorkspaceService = inject(LastUsedWorkspaceService)

  protected TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY

  selectedWorkspace = this.#workspacesAndPermissionsStore.selectedWorkspace
  availableWorkspacesForUser = this.#workspacesAndPermissionsStore.availableWorkspacesForUser

  groupedWorkspaces = computed(() => {
    const availableWorkspacesForUser = this.availableWorkspacesForUser()
    const selectedWorkspace = this.selectedWorkspace()

    if (!availableWorkspacesForUser.length) {
      return []
    }

    return availableWorkspacesForUser.reduce(
      (groups: TreeNode[], workspace: WorkspaceDefinition): TreeNode[] => {
        let group = groups.find((item) => item.key === workspace.envType)

        if (!group) {
          group = {
            key: workspace.envType,
            label: workspace.envType,
            children: [],
            expanded: true,
            selectable: false,
            leaf: false,
            type: 'group',
          } satisfies TreeNode

          groups.push(group)
        }

        group.children.push({
          key: workspace.id,
          label: workspace.name,
          data: workspace,
          selectable: selectedWorkspace?.id !== workspace.id,
          leaf: true,
          type: 'item',
        } satisfies TreeNode<WorkspaceDefinition>)

        return groups
      },
      []
    )
  })

  selectedWorkspaceTreeNode = linkedSignal(() => {
    const selectedWorkspace = this.selectedWorkspace()
    const groupedWorkspaces = this.groupedWorkspaces()

    if (!selectedWorkspace || !groupedWorkspaces.length) {
      return null
    }

    return groupedWorkspaces
      .find((group) => group.key === selectedWorkspace.envType)
      ?.children.find((child) => child.key === selectedWorkspace.id)
  })

  async onWorkspaceChange(selectedWorkspace: WorkspaceDefinition): Promise<void> {
    this.#analyticsService.trackWorkspace({
      event: { object: WorkspaceObject.WORKSPACE, action: WorkspaceAction.SELECTED },
      properties: { envType: selectedWorkspace.envType, source: 'switcher' },
    })
    // The store follows the workspace in the URL, so the navigation selects it, and a navigation that
    // fails leaves the selection where it was.
    const navigated = await this.#navigationService.navigateToWorkspace(selectedWorkspace.id)

    // Saved only once the user is in it: a guard may still cancel the navigation.
    if (navigated) {
      this.#lastUsedWorkspaceService.save(selectedWorkspace.id)
    }
  }
}
